import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, RotateCcw } from "lucide-react";
import { SeloDestaque } from "@/components/SeloDestaque";

const ABAS = [
  ["resumo", "Resumo"], ["dados", "Dados Pessoais"], ["documentos", "Documentos"],
  ["cobertura", "Cobertura"], ["score", "Score"], ["distribuicao", "Distribuição"],
  ["agenda", "Agenda"], ["os", "OS / Histórico"], ["financeiro", "Financeiro"],
  ["contratos", "Contratos"], ["ocorrencias", "Ocorrências"], ["alertas", "Alertas"], ["logs", "Logs"],
];

function KV({ k, v }) {
  return (
    <div className="border border-border rounded-lg px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-aj-text-muted">{k}</div>
      <div className="text-sm font-semibold text-aj-text break-words">{v ?? "—"}</div>
    </div>
  );
}

export default function MontadorFicha() {
  const { montadorId } = useParams();
  const [f, setF] = useState(null);
  const [aba, setAba] = useState("resumo");
  const [reativando, setReativando] = useState(false);

  const carregar = () => api.get(`/admin/montadores/${montadorId}/ficha`)
    .then(r => setF(r.data)).catch(e => toast.error(formatApiError(e)));

  useEffect(() => { carregar(); }, [montadorId]);

  const reativar = async () => {
    setReativando(true);
    try {
      await api.post(`/admin/montadores/${montadorId}/reativar`);
      toast.success("Montador reativado com sucesso!");
      await carregar();
    } catch (e) { toast.error(formatApiError(e)); }
    setReativando(false);
  };

  if (!f) return <div className="aj-card p-10 text-center text-aj-text-muted">Carregando ficha…</div>;
  const r = f.resumo || {};

  return (
    <div className="space-y-5" data-testid="montador-ficha-page">
      <Link to="/admin/montadores" className="text-sm text-aj-text-muted hover:underline inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Montadores
      </Link>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">Gestão de Montadores</div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-aj-blue-deep mt-1 flex items-center gap-2">
            {r.nome}
            {r.documentos_verificados && <ShieldCheck size={20} className="text-[#166534]" title="Documentos verificados" />}
          </h1>
          <div className="flex gap-2 mt-2 flex-wrap">
            <SeloDestaque show={r.destaque} />
            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-[#DBEAFE] text-[#142766]" data-testid="ficha-nivel">{r.nivel_experiencia}</span>
            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-aj-amber-soft text-aj-orange-hover" data-testid="ficha-ciclo">{(r.status_ciclo || "").replace(/_/g, " ")}</span>
            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-[#F4F7FB] text-aj-text-muted">Score {Number(r.score ?? 0).toFixed(0)}</span>
            {r.atividade_rede === "inativo_por_ausencia" && (
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-red-100 text-[#B91C1C]" data-testid="ficha-inativo-ausencia">Inativo por ausência</span>
            )}
          </div>
        </div>
        {r.atividade_rede === "inativo_por_ausencia" && (
          <button onClick={reativar} disabled={reativando}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-aj-blue-deep text-white hover:opacity-90 disabled:opacity-50"
            data-testid="ficha-reativar-btn">
            <RotateCcw size={15} /> {reativando ? "Reativando…" : "Reativar montador"}
          </button>
        )}
      </div>

      <div className="flex gap-1.5 flex-wrap border-b border-border pb-3">
        {ABAS.map(([k, l]) => (
          <button key={k} onClick={() => setAba(k)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold ${aba === k ? "bg-aj-blue-deep text-white" : "bg-white border border-border text-aj-text-muted hover:text-aj-blue-deep"}`}
            data-testid={`ficha-aba-${k}`}>{l}</button>
        ))}
      </div>

      <div className="aj-card p-5" data-testid={`ficha-conteudo-${aba}`}>
        {aba === "resumo" && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            <KV k="Score" v={r.score} /><KV k="Score legado" v={r.score_legado} />
            <KV k="Experiência" v={r.nivel_experiencia} /><KV k="Finalizados" v={r.servicos_finalizados_total} />
            <KV k="Ciclo" v={(r.status_ciclo || "").replace(/_/g, " ")} /><KV k="Status legado" v={r.status} />
            <KV k="Contrato" v={r.contrato_assinado ? "Assinado" : "Pendente"} />
            <KV k="Docs verificados" v={r.documentos_verificados ? "Sim" : "Não"} />
            <KV k="Retrabalhos s/ causa" v={r.retrabalhos_pendentes_classificacao} />
            <KV k="Cadastro" v={r.created_at ? new Date(r.created_at).toLocaleDateString("pt-BR") : "—"} />
          </div>
        )}
        {aba === "dados" && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {Object.entries(f.dados_pessoais || {}).map(([k, v]) => <KV key={k} k={k} v={String(v ?? "—")} />)}
          </div>
        )}
        {aba === "documentos" && (
          <div className="space-y-2">
            {(f.documentos || []).length === 0 && <div className="text-sm text-aj-text-muted">Nenhum documento enviado.</div>}
            {(f.documentos || []).map(d => (
              <div key={d.id} className="flex items-center justify-between border border-border rounded-lg px-3 py-2 text-sm">
                <span>{d.tipo} {d.subtipo ? `(${d.subtipo})` : ""}</span>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${d.status === "aprovado" ? "bg-[#DCFCE7] text-[#166534]" : d.status === "rejeitado" ? "bg-red-100 text-[#B91C1C]" : "bg-[#DBEAFE] text-[#142766]"}`}>{d.status}</span>
              </div>
            ))}
          </div>
        )}
        {aba === "cobertura" && (
          f.cobertura ? <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {["cidade_principal", "estado_principal", "raio_km", "atende_toda_cidade", "capacidade_semanal"].map(k =>
              <KV key={k} k={k} v={String(f.cobertura[k] ?? "—")} />)}
          </div> : <div className="text-sm text-aj-text-muted">Cobertura não configurada.</div>
        )}
        {aba === "score" && (
          f.score_detalhes ? <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {Object.entries(f.score_detalhes).map(([k, v]) => <KV key={k} k={k} v={String(v)} />)}
          </div> : <div className="text-sm text-aj-text-muted">Score ainda não calculado.</div>
        )}
        {aba === "distribuicao" && (
          <KV k="Interesses registrados (30 dias)" v={f.distribuicao?.interesses_30d} />
        )}
        {aba === "agenda" && (
          <ListaOS itens={f.agenda} vazio="Nenhum atendimento futuro agendado." />
        )}
        {aba === "os" && <ListaOS itens={f.os_historico} vazio="Sem OS no histórico." />}
        {aba === "financeiro" && <KV k="Serviços finalizados (total)" v={f.financeiro?.finalizados_total} />}
        {aba === "contratos" && (
          <div className="space-y-2">
            {(f.contratos || []).length === 0 && <div className="text-sm text-aj-text-muted">Nenhum contrato registrado.</div>}
            {(f.contratos || []).map(c => (
              <div key={c.id} className="text-sm border border-border rounded-lg px-3 py-2">
                {c.tipo || "Contrato"} v{c.versao} · aceito em {c.aceito_em ? new Date(c.aceito_em).toLocaleString("pt-BR") : "—"} · IP {c.ip || "—"}
              </div>
            ))}
          </div>
        )}
        {aba === "ocorrencias" && (
          <div className="space-y-2 text-sm">
            <div className="font-bold text-xs uppercase text-aj-text-muted">Faltas ({(f.ocorrencias?.faltas || []).length})</div>
            {(f.ocorrencias?.faltas || []).map((x, i) => <div key={i} className="border border-border rounded px-3 py-1.5">{x.registrada_em?.slice(0, 10)} · {x.justificada ? "justificada" : "NÃO justificada"} · {x.motivo || ""}</div>)}
            <div className="font-bold text-xs uppercase text-aj-text-muted mt-3">Ocorrências de execução ({(f.ocorrencias?.execucao || []).length})</div>
            {(f.ocorrencias?.execucao || []).map((x, i) => <div key={i} className="border border-border rounded px-3 py-1.5">{x.tipo} · {x.criado_em?.slice(0, 10)}</div>)}
          </div>
        )}
        {aba === "alertas" && (
          <div className="space-y-2 text-sm">
            {(f.alertas || []).length === 0 && <div className="text-aj-text-muted">Nenhum alerta operacional.</div>}
            {(f.alertas || []).map(a => <div key={a.id} className="border border-border rounded px-3 py-1.5">{a.nivel} · {a.pim_code} · {a.criado_em?.slice(0, 16).replace("T", " ")}</div>)}
          </div>
        )}
        {aba === "logs" && (
          <div className="space-y-1.5 text-xs font-mono">
            {(f.logs || []).map((l, i) => <div key={i} className="border-b border-border/50 pb-1">{(l.timestamp || "").slice(0, 19).replace("T", " ")} · {l.action} · {l.actor_email || l.actor_id || "sistema"}</div>)}
          </div>
        )}
      </div>
    </div>
  );
}

function ListaOS({ itens, vazio }) {
  if (!itens?.length) return <div className="text-sm text-aj-text-muted">{vazio}</div>;
  return (
    <div className="space-y-2">
      {itens.map(s => (
        <Link key={s.id} to={`/admin/servicos/${s.id}`}
          className="flex items-center justify-between border border-border rounded-lg px-3 py-2 text-sm hover:border-aj-orange transition-colors">
          <span className="font-mono text-xs">{s.pim_code}</span>
          <span className="flex-1 mx-3 truncate">{s.produto} · {s.cliente_cidade || "—"}</span>
          <span className="text-[10px] font-bold uppercase text-aj-text-muted">{(s.status || "").replace(/_/g, " ")}{s.data_confirmada ? ` · ${s.data_confirmada}` : ""}</span>
        </Link>
      ))}
    </div>
  );
}
